import { Button, Checkbox, Col, Form, Input, Row } from 'antd';
import { CheckboxChangeEvent } from 'antd/lib/checkbox';
import { Rule, RuleObject } from 'rc-field-form/lib/interface';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { WarningOutlined } from '@ant-design/icons';
import { formatPhoneNumber, isEmptyPhoneNumber, validatePhoneNumber } from '@common/helpers';
import { CustomerInfo } from '@common/models/customer-info.model';
import { addressValidator } from '@common/utils/addressValidator';

import './CustomerDataForm.scss';

interface CustomerDataFormProps {
    onCancel?: () => void;
    onSave: (customerInfo: CustomerInfo) => void;
    isSavingEstimate: boolean;
    initialCustomerData?: CustomerInfo;
    showSendEmailOption?: boolean;
}

interface CustomerFormData extends CustomerInfo {
    confirmName: boolean;
    confirmAddress: boolean;
}

const testIsNameSuspect = (name: string | undefined) => {
    return !!name?.length && (name?.trim() ?? '').split(/\s+/).length < 2;
};

const testIsAddressSuspect = (address1: string, _address2 = '', city = '', state = '', zip = '') => {
    if (!address1 || address1?.length === 0) {
        return false;
    }
    const addressValidation = addressValidator(
        address1,
        '', // ignore address2!!!
        city,
        state,
        zip
    );
    return !addressValidation.isValidAddress;
};

const phoneValidator = (_rule: RuleObject, value: string) => {
    // zero length is valid, use required to check for this
    // 7 digits simple numbers are acceptable
    // 10 digit US format is acceptable excluding invalid area codes
    // +1 formatted numbers if they parse correctly
    if (isEmptyPhoneNumber(value) || validatePhoneNumber(value, ['US'])) {
        return Promise.resolve();
    }
    return Promise.reject('Invalid phone number');
};

const nameWarningValidator = (_rule: RuleObject, value: string) => {
    if (testIsNameSuspect(value)) {
        return Promise.reject('Please verify the name is correct');
    }
    return Promise.resolve();
};

const customerMaxLength = 50;
const addressMaxLength = 255;
const cityMaxLength = 128;
const phoneMaxLength = 25;
const BLANK_FORM_DATA: CustomerFormData = {
    customerName: '',
    address1: '',
    address2: '',
    city: '',
    state: '',
    zip: '',
    phone: '',
    alternatePhone: '',
    email: '',
    sendCustomerEmail: true,
    confirmName: false,
    confirmAddress: false,
};

/**
 * The keys of the _possible_ attributes of the form that _may_ have rules
 */
type RuleKey = keyof CustomerFormData;
/**
 * An object of rules for any form attribute that requires them
 */
type NamedRule = {
    [key in RuleKey]?: Rule[];
};

/**
 * @param onCancel
 * @param onSave function(customerData) will receive the updated customerData
 * @param initialCustomerData initial data to display
 * @param isSavingEstimate true will diable entry and show spinner on button
 * @param showRemember show "remember me" options?
 */
const CustomerDataForm = ({
    onCancel,
    onSave,
    initialCustomerData,
    isSavingEstimate,
    showSendEmailOption,
}: CustomerDataFormProps): JSX.Element => {
    const { t } = useTranslation();

    const [form] = Form.useForm<CustomerFormData>();
    const [allFormData, setAllFormData] = useState<CustomerFormData>(BLANK_FORM_DATA);

    const [formHasErrors, setFormHasErrors] = useState(false);
    const [isFormValid, setIsFormValid] = useState(false);
    const [showConfirmName, setShowConfirmName] = useState(false);
    const [nameConfirmed, setNameConfirmed] = useState(false);
    const [showConfirmAddress, setShowConfirmAddress] = useState(false);
    const [addressConfirmed, setAddressConfirmed] = useState(false);

    const addressWarningValidation = useCallback(
        (_rule: RuleObject, _value: string) => {
            const { address1, city, state, zip } = form.getFieldsValue();
            if (testIsAddressSuspect(address1, '', city, state, zip)) {
                return Promise.reject('Please verify the address is correct');
            }
            return Promise.resolve();
        },
        [form]
    );

    const FormRules: NamedRule = {
        customerName: [
            { whitespace: true },
            {
                required: true,
                message: t('SAVE.MESSAGES.NAME'),
            },
            { max: customerMaxLength, message: `Must be less than ${customerMaxLength} characters` },
            {
                warningOnly: true,
                validator: nameWarningValidator,
                message: '',
            },
        ],
        address1: [
            { whitespace: true },
            { required: true, message: t('SAVE.MESSAGES.ADDRESS') },
            { max: addressMaxLength, message: `Must be less than ${addressMaxLength} characters` },
            {
                warningOnly: true,
                validator: addressWarningValidation,
                message: '',
            },
        ],
        address2: [
            { whitespace: true },
            { max: addressMaxLength, message: `Must be less than ${addressMaxLength} characters` },
        ],
        city: [
            { whitespace: true },
            { required: true, message: t('SAVE.MESSAGES.CITY') },
            { max: cityMaxLength, message: `Must be less than ${cityMaxLength} characters` },
        ],
        state: [
            { whitespace: true },
            {
                pattern: /^[a-zA-Z]{2}$/,
                message: 'Invalid state',
            },
            { len: 2 },
            { required: true, message: t('SAVE.MESSAGES.STATE') },
        ],
        zip: [
            { whitespace: true },
            { required: true, message: t('SAVE.MESSAGES.ZIP') },
            {
                pattern: /^[0-9]{5}(?:-[0-9]{4})?$/,
                message: 'Invalid zip code',
            },
        ],
        phone: [
            { whitespace: true },
            { required: true, message: t('SAVE.MESSAGES.PHONE') },
            {
                message: t('SAVE.MESSAGES.INVALID_PHONE'),
                validator: phoneValidator,
            },
            { max: phoneMaxLength, message: t('SAVE.MESSAGES.PHONE_LENGTH', { max: phoneMaxLength }) },
        ],
        alternatePhone: [
            { whitespace: true },
            {
                message: t('SAVE.MESSAGES.INVALID_PHONE'),
                validator: phoneValidator,
            },
            { max: phoneMaxLength, message: t('SAVE.MESSAGES.PHONE_LENGTH', { max: phoneMaxLength }) },
        ],
        email: [
            {
                type: 'email',
                required: true,
                message: t('SAVE.MESSAGES.EMAIL'),
            },
        ],
    };

    const onConfirmSuccess = (values: CustomerFormData) => {
        const customerInfo: CustomerInfo = {
            customerName: values.customerName?.trim(),
            email: values.email?.trim(),
            phone: values.phone?.trim(),
            alternatePhone: values.alternatePhone?.trim(),
            address1: values.address1?.trim(),
            address2: values.address2?.trim(),
            city: values.city?.trim(),
            state: values.state?.toUpperCase(),
            zip: values.zip?.trim(),
            sendCustomerEmail: !values.sendCustomerEmail, //make sure to invert it
        };
        onSave(customerInfo);
    };

    const onConfirmFailed = (errorInfo: unknown) => {
        console.warn('Form Validation Failed:', errorInfo);
    };

    const onCancelClick = () => {
        form.resetFields();
        setIsFormValid(false);
        if (typeof onCancel === 'function') {
            onCancel();
        }
    };

    const reformatPhones = useCallback(() => {
        ['phone', 'alternatePhone'].forEach((field) => {
            const value = form.getFieldValue(field);
            if (!value) {
                return;
            }
            form.setFields([{ name: field, value: formatPhoneNumber(value) }]);
        });
    }, [form]);

    const handleInputBlur = useCallback(
        async (e: React.FocusEvent<HTMLFormElement> | CheckboxChangeEvent | undefined) => {
            if (e) {
                const { id: name, value } = e.target;
                if (name) {
                    setAllFormData((prev) => ({ ...prev, [name]: value }));
                }
            }

            // force all field validation so the errors will be updated
            await form.validateFields({ validateOnly: true }).catch(() => {
                // ignore errors
                return;
            });

            const formData = form.getFieldsValue() as CustomerFormData;
            const { customerName, address1 } = formData;

            const errorsAndWarnings = form.getFieldsError();
            const errors = errorsAndWarnings.filter((value) => value.errors.length > 0).map((value) => value.name[0]);
            const hasErrors = errors.length > 0;

            const isNameSuspect = testIsNameSuspect(customerName);
            setShowConfirmName(!!(customerName?.trim() ?? '').length && isNameSuspect);
            if (!isNameSuspect) {
                setNameConfirmed(false);
            }

            const isAddressSuspect = testIsAddressSuspect(
                address1,
                formData.address2,
                formData.city,
                formData.state,
                formData.zip
            );
            setShowConfirmAddress(!!(address1?.trim() ?? '').length && isAddressSuspect);
            if (!isAddressSuspect) {
                setAddressConfirmed(false);
            }

            reformatPhones();

            setFormHasErrors(hasErrors);
        },
        [form, reformatPhones]
    );

    // deal with the initial data
    useEffect(() => {
        if (!initialCustomerData) {
            return;
        }
        handleInputBlur(undefined);
    }, [handleInputBlur, initialCustomerData]);

    useEffect(() => {
        const formData = form.getFieldsValue() as CustomerFormData;
        const { customerName, address1, city, state, zip, phone } = formData;
        const allRequired = [customerName, address1, city, state, zip, phone].reduce((result, field) => {
            return result && (field?.trim() ?? '') !== '';
        }, true);
        const allConfirmed = (showConfirmName ? nameConfirmed : true) && (showConfirmAddress ? addressConfirmed : true);
        setIsFormValid(allRequired && !formHasErrors && allConfirmed);
    }, [showConfirmName, nameConfirmed, showConfirmAddress, addressConfirmed, formHasErrors, form, allFormData]);

    // update the form with our nameConfirmed and addressConfirmed values
    // this _should_ be handled by the checked={nameConfirmed} but the form is overriding
    useEffect(() => {
        form.setFieldsValue({ confirmName: nameConfirmed, confirmAddress: addressConfirmed });
    }, [nameConfirmed, addressConfirmed, form]);

    return (
        <Form
            onBlur={handleInputBlur}
            form={form}
            disabled={isSavingEstimate}
            className="CustomerDataForm__container"
            initialValues={{
                ...initialCustomerData,
                sendCustomerEmail: !(initialCustomerData?.sendCustomerEmail ?? true),
            }}
            onFinish={onConfirmSuccess}
            onFinishFailed={onConfirmFailed}
            validateTrigger={['onBlur']} // Modify this to include onChange
            autoComplete="off"
            layout="horizontal"
            labelAlign="right"
            labelCol={{ span: 6 }}
            wrapperCol={{ span: 18 }}
        >
            <Form.Item
                label={t('SAVE.LABEL.NAME')}
                name="customerName"
                rules={FormRules.customerName}
                className="CustomerDataForm__item"
            >
                <Input className="CustomerDataForm__input" />
            </Form.Item>

            <Form.Item hidden={!showConfirmName} className="CustomerDataForm__send-email">
                <WarningOutlined className="CustomerDataForm__warning" />
                <Form.Item
                    name="confirmName"
                    valuePropName="checked"
                    noStyle
                    rules={[
                        {
                            required: !showConfirmName,
                            message: 'Please confirm the customer name is valid',
                        },
                    ]}
                >
                    <Checkbox
                        tabIndex={-1}
                        checked={nameConfirmed}
                        onChange={(e) => setNameConfirmed(e.target.checked)}
                    >
                        {t('SAVE.LABEL.CONFIRM_NAME')}
                    </Checkbox>
                </Form.Item>
            </Form.Item>

            <Form.Item
                label={t('SAVE.LABEL.ADDRESS1')}
                name={'address1'}
                className="CustomerDataForm__item"
                rules={FormRules.address1}
            >
                <Input className="CustomerDataForm__input" />
            </Form.Item>

            <Form.Item
                label={t('SAVE.LABEL.ADDRESS2')}
                name={'address2'}
                className="CustomerDataForm__item"
                rules={FormRules.address2}
            >
                <Input className="CustomerDataForm__input" />
            </Form.Item>

            <Form.Item hidden={!showConfirmAddress} className="CustomerDataForm__send-email">
                <WarningOutlined className="CustomerDataForm__warning" />
                <Form.Item
                    name="confirmAddress"
                    valuePropName="checked"
                    noStyle
                    rules={[
                        {
                            required: !showConfirmAddress,
                            message: 'Please confirm the customer address is valid',
                        },
                    ]}
                >
                    <Checkbox
                        tabIndex={-1}
                        checked={addressConfirmed}
                        onChange={(e) => setAddressConfirmed(e.target.checked)}
                    >
                        {t('SAVE.LABEL.CONFIRM_ADDRESS')}
                    </Checkbox>
                </Form.Item>
            </Form.Item>

            <Form.Item
                label={t('SAVE.LABEL.CITY')}
                name={'city'}
                className="CustomerDataForm__item"
                rules={FormRules.city}
            >
                <Input disabled className="CustomerDataForm__input" />
            </Form.Item>
            <Form.Item
                label={t('SAVE.LABEL.STATE')}
                name={'state'}
                className="CustomerDataForm__item"
                rules={FormRules.state}
            >
                <Input disabled className="CustomerDataForm__input" />
            </Form.Item>
            <Form.Item
                label={t('SAVE.LABEL.ZIP')}
                name={'zip'}
                className="CustomerDataForm__item"
                rules={FormRules.zip}
            >
                <Input className="CustomerDataForm__input" disabled />
            </Form.Item>

            <Form.Item
                label={t('SAVE.LABEL.EMAIL')}
                name="email"
                className="CustomerDataForm__item"
                rules={[
                    {
                        type: 'email',
                        required: true,
                        message: 'Invalid E-mail',
                    },
                ]}
            >
                <Input className="CustomerDataForm__input" disabled={isSavingEstimate} />
            </Form.Item>
            <Form.Item
                label={t('SAVE.LABEL.PHONE')}
                name={'phone'}
                className="CustomerDataForm__item"
                rules={FormRules.phone}
            >
                <Input className="CustomerDataForm__input" />
            </Form.Item>
            <Form.Item
                label={t('SAVE.LABEL.ALT_PHONE')}
                name={'alternatePhone'}
                className="CustomerDataForm__item"
                rules={FormRules.alternatePhone}
            >
                <Input className="CustomerDataForm__input" />
            </Form.Item>

            <Form.Item hidden={!showSendEmailOption} className="CustomerDataForm__send-email">
                <Form.Item name="sendCustomerEmail" valuePropName="checked" noStyle>
                    <Checkbox onChange={handleInputBlur}>{t('SAVE.LABEL.SEND_EMAIL')}</Checkbox>
                </Form.Item>
            </Form.Item>

            <Row>
                <Col span={24} className="CustomerDataForm__button-container">
                    <Button
                        className={'CustomerDataForm__button'}
                        type="primary"
                        loading={isSavingEstimate}
                        disabled={!isFormValid || isSavingEstimate}
                        htmlType="submit"
                        key="submit"
                    >
                        {t('SAVE.LABEL.SUBMIT')}
                    </Button>
                    <Button
                        className={'CustomerDataForm__button'}
                        type="default"
                        disabled={isSavingEstimate}
                        htmlType="button"
                        onClick={onCancelClick}
                        key="cancel"
                    >
                        {t('SAVE.LABEL.CANCEL')}
                    </Button>
                </Col>
            </Row>
        </Form>
    );
};
export default CustomerDataForm;
